import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Checkbox,
  ListItemText,
  CircularProgress,
  Autocomplete,
  Pagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  AttachMoney,
  Business,
  Home,
  CalendarToday,
  CloudUpload,
  Image as ImageIcon,
  Close,
  Work,
  PhotoCamera,
  Star,
} from '@mui/icons-material';
import api from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { exportToExcel, exportToPDF } from '../../utils/exportHelpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { extractAndTranslateError, formatNumber, formatCurrency, formatStringWithNumbers, formatDate } from '../../utils/i18nHelpers';

interface Building {
  id: number;
  name: string;
  name_ar?: string;
}

interface Flat {
  id: number;
  flat_number: string;
  building?: {
    id: number;
    name: string;
    name_ar?: string;
  };
}

interface WorkOrder {
  id: number;
  work_order_number: string;
  title: string;
  technician?: {
    id: number;
    service_type?: string;
  };
}

interface Amenity {
  id: number;
  name_en: string;
  name_ar: string;
  is_active: boolean;
}

interface Expense {
  id: number;
  expense_number: string;
  title?: string;
  service_type?: string;
  category?: string; // For backward compatibility
  amount: number;
  expense_date: string;
  vendor?: string;
  notes?: string;
  is_recurring: boolean;
  receipts?: string[];
  work_order_id?: number;
  work_order?: WorkOrder;
  building?: Building;
  flat?: Flat;
  buildings?: Building[];
  flats?: Flat[];
  amenities?: Amenity[];
}

const Expenses: React.FC = () => {
  const { can, user, technicianId } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { t, language } = useLanguage();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [flats, setFlats] = useState<Flat[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [serviceTypes, setServiceTypes] = useState<Array<{ id: number; name_en: string; name_ar: string }>>([]);
  const [technicianServiceType, setTechnicianServiceType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const perPage = 10;
  const [open, setOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    building_ids: [] as string[],
    flat_ids: [] as string[],
    amenity_ids: [] as number[],
    work_order_id: '',
    title: '',
    service_type: '',
    amount: 0,
    expense_date: new Date().toISOString().split('T')[0],
    vendor: '',
    notes: '',
    is_recurring: false,
  });
  const [invoiceImages, setInvoiceImages] = useState<File[]>([]);
  const [existingReceipts, setExistingReceipts] = useState<string[]>([]);
  const [receiptPreviews, setReceiptPreviews] = useState<string[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{ building?: boolean; flat?: boolean }>({});
  const isTechnicianUser = user?.role === 'technician' || !!technicianId;
  
  const getDisplayServiceTypeName = (value: string): string => {
    if (!value) return '';
    const st = serviceTypes.find(
      (serviceType) =>
        serviceType.name_en === value || serviceType.name_ar === value
    );
    if (!st) return value;
    return language === 'ar' ? st.name_ar : st.name_en;
  };
  
  // Camera capture state
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    fetchExpenses();
    fetchBuildings();
    fetchFlats();
    fetchWorkOrders();
    fetchServiceTypes();
    fetchAmenities();
  }, []);

  // Debounced search: re-fetch from API when searchTerm changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchExpenses(1, searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch technician service type when technicianId becomes available
  useEffect(() => {
    if (technicianId) {
      fetchTechnicianServiceType();
    }
  }, [technicianId, user]);

  // Auto-populate service_type when technicianServiceType is loaded
  useEffect(() => {
    if (technicianServiceType && !formData.service_type) {
      setFormData(prev => ({ ...prev, service_type: technicianServiceType }));
    }
  }, [technicianServiceType]);

  const fetchServiceTypes = async () => {
    try {
      const response = await api.get('/service-types');
      setServiceTypes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching service types:', error);
      setServiceTypes([]);
    }
  };

  const fetchTechnicianServiceType = async () => {
    if (!technicianId) return;

    try {
      const response = await api.get(`/technicians/${technicianId}`);
      const techData = response.data;

      // First try direct service_type field
      if (techData?.service_type) {
        setTechnicianServiceType(techData.service_type);
      }
      // Fallback: get from serviceTypes relation (first one)
      else if (techData?.service_types && techData.service_types.length > 0) {
        setTechnicianServiceType(techData.service_types[0].name_en);
      }
    } catch (error) {
      console.error('Error fetching technician service type:', error);

      // Fallback: try fetching all technicians and find the one for this user
      if (user) {
        try {
          const response = await api.get('/technicians');
          const tech = response.data.find((t: any) => t.user?.id === user.id);
          if (tech?.service_type) {
            setTechnicianServiceType(tech.service_type);
          } else if (tech?.service_types && tech.service_types.length > 0) {
            setTechnicianServiceType(tech.service_types[0].name_en);
          }
        } catch (fallbackError) {
          console.error('Error in fallback technician fetch:', fallbackError);
        }
      }
    }
  };

  const fetchExpenses = async (page: number = 1, search: string = '') => {
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
      if (search.trim()) params.append('search', search.trim());
      const response = await api.get(`/expenses?${params.toString()}`);
      console.log('Expenses API response:', response);
      // Handle paginated response
      const responseData = response.data;
      const expensesData = responseData?.data || (Array.isArray(responseData) ? responseData : []);

      // Set pagination data if available
      if (responseData?.last_page) {
        setTotalPages(responseData.last_page);
        setTotalCount(responseData.total || 0);
        setCurrentPage(responseData.current_page || page);
      } else {
        setTotalPages(1);
        setTotalCount(expensesData.length);
      }

      console.log('Expenses data:', expensesData);
      console.log('Expenses count:', expensesData.length);
      setExpenses(expensesData);
    } catch (error: any) {
      console.error('Error fetching expenses:', error);
      console.error('Error response:', error?.response);
      setExpenses([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildings = async () => {
    // Always try to fetch if user can add/edit expenses (needed for form dropdowns)
    // Even if they don't have buildings.view permission
    if (!can('buildings.view') && !can('expenses.add') && !can('expenses.edit')) {
      setBuildings([]);
      return;
    }
    
    try {
      const response = await api.get('/buildings');
      setBuildings(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      // Suppress 403 errors - expected when user doesn't have buildings.view permission
      // but still needs buildings for expense form
      if (error?.response?.status === 403) {
        // Silently fail - user doesn't have permission to view buildings
        // but they can still create/edit expenses without selecting a building
        setBuildings([]);
      } else {
        console.error('Error fetching buildings:', error);
        setBuildings([]);
      }
    }
  };

  const fetchFlats = async () => {
    // Always try to fetch if user can add/edit expenses (needed for form dropdowns)
    // Even if they don't have flats.view permission
    if (!can('flats.view') && !can('expenses.add') && !can('expenses.edit')) {
      setFlats([]);
      return;
    }

    try {
      // Request all flats for dropdown (large per_page to get all)
      const response = await api.get('/flats?per_page=1000');
      // Handle both paginated and non-paginated responses
      const flatsData = response.data?.data || response.data;
      setFlats(Array.isArray(flatsData) ? flatsData : []);
    } catch (error: any) {
      // Suppress 403 errors - expected when user doesn't have flats.view permission
      // but still needs flats for expense form
      if (error?.response?.status === 403) {
        // Silently fail - user doesn't have permission to view flats
        // but they can still create/edit expenses without selecting a flat
        setFlats([]);
      } else {
        console.error('Error fetching flats:', error);
        setFlats([]);
      }
    }
  };

  const fetchWorkOrders = async () => {
    // Only fetch work orders if user can add expenses (providers need this)
    if (!can('expenses.add') && !can('expenses.edit')) {
      setWorkOrders([]);
      return;
    }

    try {
      // Request all work orders for dropdown (large per_page to get all)
      const response = await api.get('/work-orders?per_page=1000');
      // Handle both paginated and non-paginated responses
      const workOrdersData = response.data?.data || response.data;
      setWorkOrders(Array.isArray(workOrdersData) ? workOrdersData : []);
    } catch (error: any) {
      // Suppress 403 errors - expected when user doesn't have work-orders.view permission
      if (error?.response?.status === 403) {
        setWorkOrders([]);
      } else {
        console.error('Error fetching work orders:', error);
        setWorkOrders([]);
      }
    }
  };

  const fetchAmenities = async () => {
    try {
      const response = await api.get('/amenities');
      const amenitiesData = response.data?.data || response.data;
      setAmenities(Array.isArray(amenitiesData) ? amenitiesData.filter((a: Amenity) => a.is_active) : []);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
      setAmenities([]);
    }
  };

  // Get amenities filtered by selected buildings
  const getFilteredAmenities = (): Amenity[] => {
    if (!formData.building_ids.length) return amenities;

    // Find the selected buildings and get their amenity IDs
    const selectedBuildings = buildings.filter(b => formData.building_ids.includes(b.id.toString()));
    const buildingAmenityIds = new Set<number>();

    selectedBuildings.forEach(building => {
      // Buildings have amenities as an array of IDs
      const buildingAmenities = (building as any).amenities || [];
      if (Array.isArray(buildingAmenities)) {
        buildingAmenities.forEach((id: number) => buildingAmenityIds.add(id));
      }
    });

    // If buildings have no amenities configured, show all amenities
    if (buildingAmenityIds.size === 0) return amenities;

    // Filter amenities to only those in selected buildings
    return amenities.filter(a => buildingAmenityIds.has(a.id));
  };

  const handleOpen = async (expense?: Expense) => {
    if (expense) {
      setEditingExpense(expense);
      let serviceType = expense.service_type || expense.category || technicianServiceType || '';
      
      // If expense has a work order, fetch it to get technician's service_type
      if (expense.work_order_id) {
        try {
          const selectedWorkOrder = workOrders.find(wo => wo.id === expense.work_order_id);
          if (selectedWorkOrder?.technician?.service_type) {
            serviceType = selectedWorkOrder.technician.service_type;
          } else {
            // If not in list, fetch work order details
            const response = await api.get(`/work-orders/${expense.work_order_id}`);
            if (response.data?.technician?.service_type) {
              serviceType = response.data.technician.service_type;
            }
          }
        } catch (error) {
          console.error('Error fetching work order details:', error);
        }
      }
      
      setFormData({
        building_ids: (expense.buildings || (expense.building ? [expense.building] : []))
          .map((b) => b.id.toString()),
        flat_ids: (expense.flats || (expense.flat ? [expense.flat] : []))
          .map((f) => f.id.toString()),
        amenity_ids: (expense.amenities || []).map((a) => a.id),
        work_order_id: expense.work_order_id?.toString() || '',
        title: expense.title || '',
        service_type: serviceType,
        amount: expense.amount,
        expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : new Date().toISOString().split('T')[0],
        vendor: expense.vendor || '',
        notes: expense.notes || '',
        is_recurring: expense.is_recurring,
      });
      setExistingReceipts(expense.receipts || []);
      setReceiptPreviews(expense.receipts || []);
    } else {
      setEditingExpense(null);
      setFormData({
        building_ids: [],
        flat_ids: [],
        amenity_ids: [],
        work_order_id: '',
        title: '',
        service_type: technicianServiceType || '',
        amount: 0,
        expense_date: new Date().toISOString().split('T')[0],
        vendor: '',
        notes: '',
        is_recurring: false,
      });
      setExistingReceipts([]);
      setReceiptPreviews([]);
    }
    setInvoiceImages([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingExpense(null);
    setInvoiceImages([]);
    setExistingReceipts([]);
    setReceiptPreviews([]);
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // Prevent double submission

    // Validate required fields when no work order selected
    const hasSelectedWO = !!formData.work_order_id;
    if (!hasSelectedWO) {
      const errors: { building?: boolean; flat?: boolean } = {};
      if (formData.building_ids.length === 0) errors.building = true;
      if (formData.flat_ids.length === 0) errors.flat = true;
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
    }

    setSubmitting(true);
    try {
      const formDataToSend = new FormData();

      // Add text fields
      formDataToSend.append('title', formData.title);
      formDataToSend.append('service_type', formData.service_type);
      formDataToSend.append('amount', formData.amount.toString());
      formDataToSend.append('expense_date', formData.expense_date);
      if (formData.vendor) formDataToSend.append('vendor', formData.vendor);
      formDataToSend.append('notes', formData.notes || '');
      formDataToSend.append('is_recurring', formData.is_recurring.toString());
      
      // Single building/flat (backward compatibility: use first selected)
      if (formData.building_ids.length > 0) {
        formDataToSend.append('building_id', formData.building_ids[0]);
      }
      if (formData.flat_ids.length > 0) {
        formDataToSend.append('flat_id', formData.flat_ids[0]);
      }

      // Multiple buildings/flats/amenities (new many-to-many API)
      formData.building_ids.forEach((id) => {
        formDataToSend.append('building_ids[]', id);
      });
      formData.flat_ids.forEach((id) => {
        formDataToSend.append('flat_ids[]', id);
      });
      formData.amenity_ids.forEach((id) => {
        formDataToSend.append('amenity_ids[]', id.toString());
      });
      if (formData.work_order_id) {
        formDataToSend.append('work_order_id', formData.work_order_id);
      }
      
      // Add invoice images
      invoiceImages.forEach((file) => {
        formDataToSend.append('invoice_images[]', file);
      });
      
      // Add existing receipts for update
      if (editingExpense) {
        existingReceipts.forEach((receipt) => {
          formDataToSend.append('existing_receipts[]', receipt);
        });
      }

      if (editingExpense) {
        // Use POST with _method=PUT because PHP/Laravel do not reliably parse multipart bodies on true PUT
        await api.post(`/expenses/${editingExpense.id}?_method=PUT`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        notifySuccess('Expense updated successfully');
      } else {
        await api.post('/expenses', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        notifySuccess('Expense created successfully');
      }
      fetchExpenses(currentPage, searchTerm);
      handleClose();
    } catch (error) {
      console.error('Error saving expense:', error);
      const errorMessage = extractAndTranslateError(error, language, t);
      notifyError(errorMessage || t('expenses.messages.save.errorDefault'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await api.delete(`/expenses/${id}`);
        fetchExpenses(currentPage, searchTerm);
        notifySuccess('Expense deleted successfully');
      } catch (error) {
        console.error('Error deleting expense:', error);
        const errorMessage = extractAndTranslateError(error, language, t);
        notifyError(errorMessage || t('expenses.messages.delete.errorDefault'));
      }
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    fetchExpenses(page, searchTerm);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const numericFields = ['amount'];

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    if (type === 'number' && numericFields.includes(name)) {
      const sanitized = value === '' ? '' : Math.max(0, Number(value));
      setFormData(prev => ({ ...prev, [name]: sanitized }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'maintenance': return 'primary';
      case 'utilities': return 'info';
      case 'repairs': return 'warning';
      case 'cleaning': return 'success';
      case 'security': return 'error';
      case 'admin': return 'default';
      default: return 'default';
    }
  };

  const getFilteredFlats = () => {
    if (!formData.building_ids.length) return flats;
    const set = new Set(formData.building_ids);
    return flats.filter((flat) => flat.building && set.has(flat.building.id.toString()));
  };

  const handleInvoiceImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setInvoiceImages((prev) => [...prev, ...files]);
      
      // Create previews
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptPreviews((prev) => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemoveInvoiceImage = (index: number) => {
    setInvoiceImages((prev) => prev.filter((_, i) => i !== index));
    setReceiptPreviews((prev) => {
      const newPreviews = [...prev];
      URL.revokeObjectURL(newPreviews[index]);
      return newPreviews.filter((_, i) => i !== index);
    });
  };

  // Camera capture functions
  const openCamera = async () => {
    setCameraOpen(true);
    try {
      // Try back camera first (for mobile)
      let mediaStream: MediaStream | null = null;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
      } catch (envError) {
        // Fallback to any available camera
        console.log('Back camera not available, trying default camera');
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({ 
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          });
        } catch (fallbackError) {
          // Last resort - any video
          mediaStream = await navigator.mediaDevices.getUserMedia({ 
            video: true
          });
        }
      }
      
      if (mediaStream) {
        setStream(mediaStream);
      } else {
        setCameraOpen(false);
        notifyError(t('expenses.camera.error.access'));
      }
    } catch (error: any) {
      console.error('Error accessing camera:', error);
      setCameraOpen(false);
      const errorMessage = error?.name === 'NotAllowedError' 
        ? t('expenses.camera.error.permission')
        : error?.name === 'NotFoundError'
        ? t('expenses.camera.error.notFound')
        : t('expenses.camera.error.generic');
      notifyError(errorMessage);
    }
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob: Blob | null) => {
      if (!blob) return;
      
      const file = new File([blob], `expense-receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setInvoiceImages(prev => [...prev, file]);
      setReceiptPreviews(prev => [...prev, URL.createObjectURL(file)]);
      
      closeCamera();
    }, 'image/jpeg', 0.9);
  };

  // Effect to handle video stream when it changes
  useEffect(() => {
    if (stream && videoRef.current) {
      const video = videoRef.current;
      video.srcObject = stream;
      video.play().catch((error: any) => {
        console.error('Error playing video:', error);
      });
    } else if (videoRef.current && !stream) {
      videoRef.current.srcObject = null;
    }
  }, [stream, cameraOpen]);

  const handleRemoveExistingReceipt = (receipt: string) => {
    setExistingReceipts((prev) => prev.filter((r) => r !== receipt));
    setReceiptPreviews((prev) => prev.filter((r) => r !== receipt));
  };

  const getImageUrl = (path: string) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const storageBase = (api.defaults.baseURL as string || '').replace(/\/api\/?$/, '');
    return `${storageBase}/storage/${path}`;
  };

  if (loading) {
    return <div>{t('expenses.loading')}</div>;
  }

  const hasSelectedWorkOrder = !!formData.work_order_id;

  // Search is now handled server-side; use expenses directly
  const filteredExpenses = expenses;

  const handleExportExcel = async () => {
    if (!filteredExpenses.length) {
      notifyError(t('expenses.export.noData'));
      return;
    }
    const rows = filteredExpenses.map((e) => {
      const buildingNames =
        e.buildings && e.buildings.length
          ? e.buildings.map((b) => (language === 'ar' && b.name_ar ? b.name_ar : b.name)).join(', ')
          : (language === 'ar' && e.building?.name_ar ? e.building.name_ar : e.building?.name) || '';

      const flatNames =
        e.flats && e.flats.length
          ? e.flats
              .map((f) => {
                const buildingName = f.building ? (language === 'ar' && f.building.name_ar ? f.building.name_ar : f.building.name) : '';
                return buildingName ? `${buildingName} - ${f.flat_number}` : f.flat_number;
              })
              .join(', ')
          : e.flat?.flat_number || '';

      const serviceTypeDisplay = e.service_type ? (() => {
        const serviceTypeObj = serviceTypes.find(st => st.name_en === e.service_type);
        return serviceTypeObj ? (language === 'ar' ? serviceTypeObj.name_ar : serviceTypeObj.name_en) : (e.service_type || '');
      })() : (e.category || '');

      return {
        [t('expenses.export.columns.number')]: e.expense_number,
        [t('expenses.export.columns.serviceType')]: serviceTypeDisplay,
        [t('expenses.export.columns.amount')]: e.amount,
        [t('expenses.export.columns.date')]: e.expense_date,
        [t('expenses.export.columns.building')]: buildingNames,
        [t('expenses.export.columns.flat')]: flatNames,
        [t('expenses.export.columns.workOrder')]: e.work_order?.work_order_number || '',
      };
    });
    await exportToExcel(rows, 'expenses');
    notifySuccess(t('expenses.export.excel.success'));
  };

  const handleExportPDF = async () => {
    if (!filteredExpenses.length) {
      notifyError(t('expenses.export.noData'));
      return;
    }
    const columns = [
      t('expenses.export.columns.number'),
      t('expenses.export.columns.serviceType'),
      t('expenses.export.columns.amount'),
      t('expenses.export.columns.date'),
      t('expenses.export.columns.building'),
      t('expenses.export.columns.flat'),
      t('expenses.export.columns.workOrder')
    ];
    const data = filteredExpenses.map((e) => {
      const buildingNames =
        e.buildings && e.buildings.length
          ? e.buildings.map((b) => (language === 'ar' && b.name_ar ? b.name_ar : b.name)).join(', ')
          : (language === 'ar' && e.building?.name_ar ? e.building.name_ar : e.building?.name) || '';

      const flatNames =
        e.flats && e.flats.length
          ? e.flats
              .map((f) => {
                const buildingName = f.building ? (language === 'ar' && f.building.name_ar ? f.building.name_ar : f.building.name) : '';
                return buildingName ? `${buildingName} - ${f.flat_number}` : f.flat_number;
              })
              .join(', ')
          : e.flat?.flat_number || '';

      const serviceTypeDisplay = e.service_type ? (() => {
        const serviceTypeObj = serviceTypes.find(st => st.name_en === e.service_type);
        return serviceTypeObj ? (language === 'ar' ? serviceTypeObj.name_ar : serviceTypeObj.name_en) : (e.service_type || '');
      })() : (e.category || '');

      return [
        e.expense_number,
        serviceTypeDisplay,
        e.amount,
        e.expense_date,
        buildingNames,
        flatNames,
        e.work_order?.work_order_number || '',
      ];
    });
    await exportToPDF(columns, data, 'expenses', language, t('expenses.title'));
    notifySuccess(t('expenses.export.pdf.success'));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4">{t('expenses.title')}</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder={t('expenses.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {can('expenses.export-excel') && (
            <Button variant="outlined" onClick={handleExportExcel}>
              {t('expenses.export.excel')}
            </Button>
          )}
          {can('expenses.export-pdf') && (
            <Button variant="outlined" onClick={handleExportPDF}>
              {t('expenses.export.pdf')}
            </Button>
          )}
          {can('expenses.add') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpen()}
            >
              {t('expenses.actions.add')}
            </Button>
          )}
        </Box>
      </Box>

      {expenses.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {t('expenses.list.noData')}
          </Typography>
        </Box>
      )}
      
      {filteredExpenses.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('expenses.list.showing').replace('{{count}}', String(filteredExpenses.length))}
        </Typography>
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredExpenses.map((expense) => (
          <Card key={expense.id}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Box>
                  <Typography variant="h6">#{formatStringWithNumbers(expense.expense_number, language)}</Typography>
                  {expense.title && (
                    <Typography variant="subtitle1" fontWeight="medium">
                      {expense.title}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {(() => {
                      const serviceType = expense.service_type || expense.category || '';
                      const serviceTypeObj = serviceTypes.find(st => st.name_en === serviceType);
                      return serviceTypeObj ? (language === 'ar' ? serviceTypeObj.name_ar : serviceTypeObj.name_en).toUpperCase() : serviceType.toUpperCase();
                    })()}
                  </Typography>
                </Box>
                <Box>
                  {can('expenses.edit') && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpen(expense)}
                    >
                      <Edit />
                    </IconButton>
                  )}
                  {can('expenses.delete') && (
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(expense.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              </Box>
              
              {expense.buildings && expense.buildings.length > 0 && (
                <Box display="flex" alignItems="center" mb={1}>
                  <Business fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {expense.buildings.map((b) => b.name).join(', ')}
                  </Typography>
                </Box>
              )}
              
              {expense.flats && expense.flats.length > 0 && (
                <Box display="flex" alignItems="center" mb={1}>
                  <Home fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {expense.flats
                      .map((f) =>
                        f.building?.name
                          ? `${f.building.name} - ${formatStringWithNumbers(f.flat_number, language)}`
                          : formatStringWithNumbers(f.flat_number, language)
                      )
                      .join(', ')}
                  </Typography>
                </Box>
              )}

              {expense.amenities && expense.amenities.length > 0 && (
                <Box display="flex" alignItems="center" mb={1}>
                  <Star fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {expense.amenities
                      .map((a) => (language === 'ar' ? a.name_ar : a.name_en))
                      .join(', ')}
                  </Typography>
                </Box>
              )}

              {expense.work_order && (
                <Box display="flex" alignItems="center" mb={1}>
                  <Work fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {t('expenses.list.workOrder')
                      .replace('{{number}}', expense.work_order.work_order_number)
                      .replace('{{title}}', expense.work_order.title)}
                  </Typography>
                </Box>
              )}
              
              <Box display="flex" gap={1} mb={2}>
                <Chip
                  label={(() => {
                    const serviceType = expense.service_type || expense.category || '';
                    const serviceTypeObj = serviceTypes.find(st => st.name_en === serviceType);
                    return serviceTypeObj ? (language === 'ar' ? serviceTypeObj.name_ar : serviceTypeObj.name_en).toUpperCase() : serviceType.toUpperCase();
                  })()}
                  size="small"
                  color={getCategoryColor(expense.service_type || expense.category || '') as any}
                />
                {expense.is_recurring && (
                  <Chip
                    label="RECURRING"
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
              
              <Box display="flex" alignItems="center" mb={1}>
                <CalendarToday fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {formatDate(expense.expense_date, language)}
                </Typography>
              </Box>
              
              <Typography variant="h6" color="primary" mb={1}>
                {formatCurrency(expense.amount, language)}
              </Typography>
              
              {expense.vendor && (
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('expenses.list.vendor')}</strong> {expense.vendor}
                </Typography>
              )}
              
              {expense.receipts && expense.receipts.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" mb={1}>
                    {t('expenses.list.invoiceImages')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {expense.receipts.map((receipt, index) => (
                      <Box key={index} sx={{ width: { xs: 'calc(50% - 4px)', sm: 'calc(33.333% - 7px)', md: 'calc(25% - 9px)' } }}>
                        <Paper
                          sx={{
                            position: 'relative',
                            padding: 1,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.8 },
                          }}
                          onClick={() => window.open(getImageUrl(receipt), '_blank')}
                        >
                          <img
                            src={getImageUrl(receipt)}
                            alt={`Invoice ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '150px',
                              objectFit: 'cover',
                              borderRadius: 4,
                            }}
                          />
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('common.pagination.showing') || 'Showing'} {expenses.length} {t('common.pagination.of') || 'of'} {totalCount} {t('common.pagination.items') || 'items'}
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingExpense ? t('expenses.dialog.editTitle') : t('expenses.dialog.addTitle')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {workOrders.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>{t('expenses.dialog.workOrder')}</InputLabel>
                  <Select
                    value={formData.work_order_id}
                    label={t('expenses.dialog.workOrder')}
                    onChange={async (e) => {
                      const workOrderId = e.target.value;
                      const newFormData = {
                        ...formData,
                        work_order_id: workOrderId,
                        // when linking to a work order, clear building/flat
                        building_ids: [],
                        flat_ids: [],
                      };
                      
                      // If work order is selected, fetch its details to get technician's service_type
                      if (workOrderId) {
                        try {
                          const selectedWorkOrder = workOrders.find(wo => wo.id.toString() === workOrderId);
                          if (selectedWorkOrder?.technician?.service_type) {
                            newFormData.service_type = selectedWorkOrder.technician.service_type;
                          } else {
                            // If technician info not in list, fetch work order details
                            const response = await api.get(`/work-orders/${workOrderId}`);
                            if (response.data?.technician?.service_type) {
                              newFormData.service_type = response.data.technician.service_type;
                            }
                          }
                        } catch (error) {
                          console.error('Error fetching work order details:', error);
                        }
                      } else {
                        // If no work order selected, reset service_type to technician's default or empty
                        newFormData.service_type = technicianServiceType || '';
                      }
                      
                      setFormData(newFormData);
                    }}
                  >
                    <MenuItem value="">{t('expenses.dialog.none')}</MenuItem>
                    {workOrders.map((wo) => (
                      <MenuItem key={wo.id} value={wo.id.toString()}>
                        {wo.work_order_number} - {wo.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              {!hasSelectedWorkOrder && (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <FormControl fullWidth error={formErrors.building}>
                    <InputLabel>{t('expenses.dialog.building')} *</InputLabel>
                    <Select
                      multiple
                      value={formData.building_ids}
                      label={t('expenses.dialog.building') + ' *'}
                      onChange={(e) => {
                        const value = e.target.value;
                        const ids =
                          typeof value === 'string'
                            ? value.split(',')
                            : (value as string[]);
                        setFormData({
                          ...formData,
                          building_ids: ids,
                          flat_ids: [],
                          amenity_ids: [], // Reset amenities when buildings change
                        });
                        if (ids.length > 0) setFormErrors(prev => ({ ...prev, building: false }));
                      }}
                      renderValue={(selected) =>
                        (selected as string[])
                          .map((id) => {
                            const b = buildings.find((b) => b.id.toString() === id);
                            if (!b) return '';
                            if (language === 'ar' && (b as any).name_ar) {
                              return (b as any).name_ar as string;
                            }
                            return b.name;
                          })
                          .filter(Boolean)
                          .join(', ')
                      }
                    >
                      {buildings.map((building) => (
                        <MenuItem key={building.id} value={building.id.toString()}>
                          <Checkbox
                            checked={formData.building_ids.includes(
                              building.id.toString()
                            )}
                          />
                          <ListItemText
                            primary={
                              language === 'ar' && (building as any).name_ar
                                ? (building as any).name_ar as string
                                : building.name
                            }
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth error={formErrors.flat}>
                    <InputLabel>{t('expenses.dialog.flat')} *</InputLabel>
                    <Select
                      multiple
                      value={formData.flat_ids}
                      label={t('expenses.dialog.flat') + ' *'}
                      onChange={(e) => {
                        const value = e.target.value;
                        const ids =
                          typeof value === 'string'
                            ? value.split(',')
                            : (value as string[]);
                        setFormData({
                          ...formData,
                          flat_ids: ids,
                        });
                        if (ids.length > 0) setFormErrors(prev => ({ ...prev, flat: false }));
                      }}
                      renderValue={(selected) =>
                        (selected as string[])
                          .map((id) =>
                            getFilteredFlats().find((f) => f.id.toString() === id)
                          )
                          .filter(Boolean)
                          .map((f) =>
                            f!.building?.name
                              ? `${f!.building!.name} - ${f!.flat_number}`
                              : f!.flat_number
                          )
                          .join(', ')
                      }
                    >
                      {getFilteredFlats().map((flat) => (
                        <MenuItem key={flat.id} value={flat.id.toString()}>
                          <Checkbox
                            checked={formData.flat_ids.includes(flat.id.toString())}
                          />
                          <ListItemText
                            primary={
                              flat.building
                                ? `${flat.building.name} - ${formatStringWithNumbers(flat.flat_number, language)}`
                                : formatStringWithNumbers(flat.flat_number, language)
                            }
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Amenities selection */}
              {!hasSelectedWorkOrder && amenities.length > 0 && (
                <Autocomplete
                  multiple
                  options={getFilteredAmenities()}
                  disableCloseOnSelect
                  getOptionLabel={(option) =>
                    language === 'ar' ? option.name_ar : option.name_en
                  }
                  value={amenities.filter((a) => formData.amenity_ids.includes(a.id))}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      amenity_ids: newValue.map((a) => a.id),
                    });
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} sx={{ mr: 1 }} />
                      {language === 'ar' ? option.name_ar : option.name_en}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('expenses.dialog.amenities') || 'Amenities'}
                      placeholder={t('expenses.dialog.searchAmenities') || 'Search amenities...'}
                    />
                  )}
                />
              )}
              
              <TextField
                fullWidth
                label={t('expenses.dialog.title')}
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                {isTechnicianUser || hasSelectedWorkOrder ? (
                  <TextField
                    fullWidth
                    label={t('expenses.dialog.serviceType') || 'Service Type'}
                    value={
                      hasSelectedWorkOrder
                        ? getDisplayServiceTypeName(formData.service_type)
                        : getDisplayServiceTypeName(technicianServiceType || formData.service_type)
                    }
                    disabled
                    helperText={
                      hasSelectedWorkOrder
                        ? t('expenses.dialog.serviceTypeFromWorkOrder') ||
                          'Service type is set from the selected work order'
                        : undefined
                    }
                  />
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>{t('expenses.dialog.serviceType') || 'Service Type'}</InputLabel>
                    <Select
                      value={formData.service_type}
                      label={t('expenses.dialog.serviceType') || 'Service Type'}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                    >
                      {serviceTypes.map((serviceType) => (
                        <MenuItem key={serviceType.id} value={serviceType.name_en}>
                          {language === 'ar' ? serviceType.name_ar : serviceType.name_en}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  label={t('expenses.dialog.amount')}
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Box>
              
              <TextField
                fullWidth
                label={t('expenses.dialog.expenseDate')}
                name="expense_date"
                type="date"
                value={formData.expense_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
              
              <TextField
                fullWidth
                label={t('expenses.dialog.vendor')}
                name="vendor"
                value={formData.vendor}
                onChange={handleInputChange}
              />
              
              <TextField
                fullWidth
                label={t('expenses.dialog.notes')}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
              
              <Box>
                <Typography variant="subtitle2" mb={1}>
                  {t('expenses.dialog.invoice.label')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    sx={{ flex: 1 }}
                  >
                    {t('expenses.dialog.invoice.button')}
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handleInvoiceImageChange}
                    />
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    onClick={openCamera}
                    sx={{ flex: 1 }}
                  >
                    {t('expenses.dialog.invoice.camera')}
                  </Button>
                </Box>
                
                {(receiptPreviews.length > 0 || existingReceipts.length > 0) && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {existingReceipts.map((receipt, index) => (
                      <Box key={`existing-${index}`} sx={{ width: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 11px)' } }}>
                        <Paper
                          sx={{
                            position: 'relative',
                            padding: 1,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveExistingReceipt(receipt)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'error.dark' },
                              zIndex: 1,
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                          <img
                            src={getImageUrl(receipt)}
                            alt={`Receipt ${index + 1}`}
                            onClick={() => setPreviewImage(getImageUrl(receipt))}
                            style={{
                              width: '100%',
                              height: '150px',
                              objectFit: 'cover',
                              borderRadius: 4,
                              cursor: 'pointer',
                            }}
                          />
                        </Paper>
                      </Box>
                    ))}
                    {receiptPreviews.slice(existingReceipts.length).map((preview, index) => (
                      <Box key={`new-${index}`} sx={{ width: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 11px)' } }}>
                        <Paper
                          sx={{
                            position: 'relative',
                            padding: 1,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveInvoiceImage(index)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'error.dark' },
                              zIndex: 1,
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                          <img
                            src={preview.startsWith('blob:') || preview.startsWith('data:') ? preview : getImageUrl(preview)}
                            alt={`New receipt ${index + 1}`}
                            onClick={() => setPreviewImage(preview.startsWith('blob:') || preview.startsWith('data:') ? preview : getImageUrl(preview))}
                            style={{
                              width: '100%',
                              height: '150px',
                              objectFit: 'cover',
                              borderRadius: 4,
                              cursor: 'pointer',
                            }}
                          />
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>{t('expenses.dialog.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={20} /> : null}>
              {editingExpense ? t('expenses.dialog.update') : t('expenses.dialog.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Camera Dialog */}
      <Dialog
        open={cameraOpen}
        onClose={closeCamera}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'black',
            maxWidth: '90vw',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t('expenses.camera.dialog.title')}</Typography>
          <IconButton onClick={closeCamera} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {stream ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  maxHeight: '600px',
                  borderRadius: 8,
                }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </>
          ) : (
            <Typography variant="body1" sx={{ color: 'white', py: 4 }}>
              {t('expenses.camera.loading')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button onClick={closeCamera} variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>
            {t('expenses.camera.cancel')}
          </Button>
          <Button
            onClick={capturePhoto}
            variant="contained"
            disabled={!stream}
            startIcon={<PhotoCamera />}
          >
            {t('expenses.camera.capture')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="lg">
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', bgcolor: 'black' }}>
          <IconButton onClick={() => setPreviewImage(null)} sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
            <Close />
          </IconButton>
          {previewImage && <img src={previewImage} alt="Preview" style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }} />}
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default Expenses;